import { ReactNode, useState } from 'react'
import { Menu, toastManager } from '@systemeio/components'
import {
  XCloseIcon,
  Pencil01Icon,
  ReportMessageIcon,
  DotsHorizontalIcon,
  Trash02Icon,
} from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Confirmation from 'shared/components/confirmation-modal'
import { featureFlagsClient } from 'shared/feature-flags/client'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useReportPost } from 'modules/community/api/report-post'
import { PostUpdateModal } from 'modules/community/components/manage-post/update-post'
import { ReportContentModal } from 'modules/community/components/report-content/report-content-modal'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { RemoveRecentActivitiesCheckbox } from 'modules/community/post/remove-recent-activities-checkbox'
import type { PostInterface } from 'modules/community/types/post-interface'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'

interface PostActionsMenuProps {
  post: PostInterface
  deletePost?: () => Promise<void>
  deletePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  declinePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  className?: string
}

export function PostActionsMenu({
  post,
  deletePost,
  deletePostAndBanUser,
  declinePostAndBanUser,
  className,
}: PostActionsMenuProps): ReactNode {
  const { user } = useUser()
  const { member, isUserMember } = useIsUserMember()
  const { t } = useTranslation()
  const router = useRouter()
  const communityPath =
    typeof router.query.communityPath === 'string' ? router.query.communityPath : ''

  const [isRemovePostOpened, setIsRemovePostOpened] = useState(false)
  const [isRemovePostAndBanUserOpened, setIsRemovePostAndBanUserOpened] = useState(false)
  const [removeRecentActivitiesChecked, setRemoveRecentActivitiesChecked] = useState(false)
  const [isDeclinePostAndBanUserOpened, setIsDeclinePostAndBanUserOpened] = useState(false)
  const [isOpenPostUpdate, setIsOpenPostUpdate] = useState(false)
  const [isReportOpened, setIsReportOpened] = useState(false)

  const reportPostMutation = useReportPost({
    communityPath,
    postId: post.id,
  })

  const [isPostUpdatable, isPostDeletable, isPostAuthorBannable, isPostDeclinable] = hasPermissions(
    user,
    'posts',
    ['update', 'delete', 'ban', 'decline'],
    {
      post,
      member,
    },
  )

  const isPostDeclinableAndAuthorBannable = isPostDeclinable && isPostAuthorBannable

  const isOwnPost =
    Boolean(user && isAuthorizedUser(user) && user.id === post.userId) ||
    member?.userId === post.userId

  const isReportsEnabled = featureFlagsClient.isOn(featureFlagKeys.enableCommunityReports)
  const canReportPost =
    isReportsEnabled &&
    Boolean(isUserMember) &&
    Boolean(communityPath) &&
    post.status === PostStatusEnum.Approved &&
    !isOwnPost

  if (
    !(
      isPostUpdatable ||
      isPostDeletable ||
      isPostAuthorBannable ||
      isPostDeclinableAndAuthorBannable ||
      canReportPost
    )
  ) {
    return null
  }

  return (
    <>
      <div className="relative flex">
        <Menu
          trigger={
            <button type="button">
              <DotsHorizontalIcon className="size-5 text-text-primary" />
            </button>
          }
          triggerProps={{
            className: 'flex size-10 flex-row items-center justify-center',
          }}
          className={className}
          items={[
            ...(isPostDeletable && deletePost
              ? [
                  {
                    id: 'remove-post',
                    label: (
                      <span className="flex w-full items-center gap-1.5">
                        <XCloseIcon className="size-5" />
                        {t('community.actions.remove_post.label')}
                      </span>
                    ),
                    onSelect: () => {
                      setIsRemovePostOpened(true)
                    },
                  },
                ]
              : []),
            ...(isPostAuthorBannable && deletePostAndBanUser
              ? [
                  {
                    id: 'delete-and-ban-user',
                    label: (
                      <span className="flex w-full items-center gap-1.5">
                        <Trash02Icon className="size-5" />
                        {t('community.actions.delete_and_ban_user.label')}
                      </span>
                    ),
                    onSelect: () => {
                      setIsRemovePostAndBanUserOpened(true)
                    },
                  },
                ]
              : []),
            ...(isPostDeclinableAndAuthorBannable && declinePostAndBanUser
              ? [
                  {
                    id: 'decline-and-ban-user',
                    label: (
                      <span className="flex w-full items-center gap-1.5">
                        <Trash02Icon className="size-5" />
                        {t('community.actions.decline_and_ban_user.label')}
                      </span>
                    ),
                    onSelect: () => {
                      setIsDeclinePostAndBanUserOpened(true)
                    },
                  },
                ]
              : []),
            ...(isPostUpdatable
              ? [
                  {
                    id: 'update-post',
                    label: (
                      <span className="flex w-full items-center gap-1.5">
                        <Pencil01Icon className="size-5" />
                        {t('community.actions.update.label')}
                      </span>
                    ),
                    onSelect: () => {
                      setIsOpenPostUpdate(true)
                    },
                  },
                ]
              : []),
            ...(canReportPost
              ? [
                  {
                    id: 'report-post',
                    label: (
                      <span className="flex w-full items-center gap-1.5">
                        <ReportMessageIcon className="size-5" />
                        {t('community.report.menu_label')}
                      </span>
                    ),
                    onSelect: () => {
                      setIsReportOpened(true)
                    },
                  },
                ]
              : []),
          ]}
        />
      </div>
      {isRemovePostOpened && (
        <Confirmation
          onConfirm={() => {
            void deletePost?.()
              .then(() => {
                setIsRemovePostOpened(false)
                toastManager.add({
                  type: 'success',
                  title: t('community.actions.remove_post.success'),
                })
              })
              .catch(error => {
                toastManager.add({
                  type: 'error',
                  title: t('community.actions.remove_post.failed'),
                })
                throw error
              })
          }}
          onCloseModal={() => {
            setIsRemovePostOpened(false)
          }}
          confirmationContent={t('settings.form.delete_post_confirmation.label')}
        />
      )}
      {isRemovePostAndBanUserOpened && (
        <Confirmation
          onConfirm={() => {
            void deletePostAndBanUser?.(removeRecentActivitiesChecked)
              .then(() => {
                toastManager.add({
                  type: 'success',
                  title: t('community.actions.remove_post_and_ban_user.success'),
                })
                setIsRemovePostAndBanUserOpened(false)
              })
              .catch(error => {
                toastManager.add({
                  type: 'error',
                  title: t('community.actions.remove_post_and_ban_user.failed'),
                })
                throw error
              })
          }}
          onCloseModal={() => {
            setIsRemovePostAndBanUserOpened(false)
            setRemoveRecentActivitiesChecked(false)
          }}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.delete_post_and_ban_user_confirmation.label')}
              memberDisplayName={post.author?.displayName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => {
                setRemoveRecentActivitiesChecked(prev => !prev)
              }}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isDeclinePostAndBanUserOpened && (
        <Confirmation
          onConfirm={() => {
            void declinePostAndBanUser?.(removeRecentActivitiesChecked)
              .then(() => {
                toastManager.add({
                  type: 'success',
                  title: t('community.actions.decline_post_and_ban_user.success'),
                })
                setIsDeclinePostAndBanUserOpened(false)
              })
              .catch(error => {
                toastManager.add({
                  type: 'error',
                  title: t('community.actions.decline_post_and_ban_user.failed'),
                })
                throw error
              })
          }}
          onCloseModal={() => {
            setIsDeclinePostAndBanUserOpened(false)
            setRemoveRecentActivitiesChecked(false)
          }}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.decline_post_and_ban_user_confirmation.label')}
              memberDisplayName={post.author?.displayName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => {
                setRemoveRecentActivitiesChecked(prev => !prev)
              }}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isOpenPostUpdate && (
        <PostUpdateModal
          post={post}
          open={isOpenPostUpdate}
          onClose={() => {
            setIsOpenPostUpdate(false)
          }}
        />
      )}
      <ReportContentModal
        opened={isReportOpened}
        onClose={() => {
          setIsReportOpened(false)
        }}
        targetType="post"
        isSubmitting={reportPostMutation.isPending}
        onSubmit={async body => {
          await reportPostMutation.mutateAsync(body)
        }}
      />
    </>
  )
}
